import { css, keyframes } from '@emotion/react';
import type { IconProps, MotionPropsFor } from '@hover/blueprint';
import { Icon, forwardRef, motion, useImage } from '@hover/blueprint';
import { hRefresh, iMoreHorizontal } from '@hover/icons';
import { includes } from '@hover/utilities';
import { AnimatePresence } from 'framer-motion';
import React from 'react';

import { Skeleton } from 'src/shared/components/Skeleton/Skeleton';
import { presenceVariants } from 'src/shared/utils/motion';

import type { DesignerMaterialProduct } from '../../types';

import { PaletteTile, type PaletteTileProps } from './PaletteTile';

export interface PaletteMaterialProps extends Omit<PaletteTileProps, 'label'> {
  disableRefresh?: boolean;
  material?: DesignerMaterialProduct;
  isRefreshable?: boolean;
}

const presence = presenceVariants({
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  initial: { opacity: 0 },
});

const MotionIcon = motion(Icon);

const iconProps = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  margin: '0',
  ...presence,
} satisfies Partial<MotionPropsFor<IconProps>>;

const fade = keyframes`
  0% { opacity: 0.9; }
  50%, 90% { opacity: 0.25; }
  100% { opacity: 0.9; }
`;

const duration = 0.6;

const loadingStyles = css([
  css`
    & circle {
      animation: ${fade} ${duration}s ease-in-out infinite;
    }
  `,
  Array.from({ length: 3 }).map(
    (_, index) => css`
      circle:nth-of-type(${index + 1}) {
        animation-delay: ${(duration / 3) * index}s;
      }
    `,
  ),
]);

const SelectionIndicator = ({ isRefreshable }: { isRefreshable?: boolean }) => (
  <AnimatePresence initial={false} mode="popLayout">
    {isRefreshable ? (
      <MotionIcon icon={hRefresh} key="regenerate" {...iconProps} />
    ) : (
      <MotionIcon
        css={loadingStyles}
        height="65%"
        icon={iMoreHorizontal}
        key="generating"
        strokeWidth="2px"
        width="65%"
        {...iconProps}
      />
    )}
  </AnimatePresence>
);

const PaletteMaterial = forwardRef<PaletteMaterialProps, 'div'>(
  ({ disableRefresh, material, isRefreshable, ...props }, ref) => {
    const { id, primaryLabel, secondaryLabel, thumbnail } = material ?? {};

    const isLoadingImage = includes(
      ['loading', 'pending'],
      useImage({ src: thumbnail }),
    );

    return (
      <PaletteTile
        label={primaryLabel}
        selectionIndicator={
          !disableRefresh ? (
            <SelectionIndicator isRefreshable={isRefreshable} />
          ) : undefined
        }
        subLabel={secondaryLabel}
        tileProps={{ padding: 0 }}
        value={String(id)}
        {...props}
        ref={ref}
      >
        <Skeleton
          backgroundImage={thumbnail}
          backgroundSize="cover"
          borderRadius="inherit"
          height="100%"
          isLoaded={!isLoadingImage}
          width="100%"
        />
      </PaletteTile>
    );
  },
);

export { PaletteMaterial };
